import AppConfig from '@/AppConfig';
import { LoadingState, Plan, SavedPaymentMethod, Subscription, SubscriptionState } from '@/classes/stripe';
import { setPaidIntentSignupMarker } from '@/Services/PaidIntentSignupMarker';
import PaymentMethodService from '@/Services/PaymentMethod/PaymentMethodService';
import { getDefaultPaymentMethod, getOtherSavedMethods } from '@/Services/PaymentMethod/paymentMethodList';
import StripeService from '@/Services/Subscription/StripeService';
import store, { RootState } from '@/store';
import * as Sentry from '@sentry/vue';
import axios, { AxiosError } from 'axios';
import { Module } from 'vuex';

const SubscriptionStore: Module<SubscriptionState, RootState> = {
  namespaced: true,

  state: new SubscriptionState(),

  mutations: {
    loadingState(state, { loading, isLoading }: { loading: string; isLoading: boolean }) {
      state.showLoading[loading as keyof LoadingState] = isLoading;
    },
    subscriptionPlans(state, plans) {
      state.plans = plans.map((item: Plan) => new Plan(item)) || [];
    },
    subscriptionSelectedPlan(state, plan) {
      state.selectedPlan = new Plan(plan);
    },
    activeSubscription(state, subscription) {
      state.activeSubscription = new Subscription(subscription);
    },
    activePlan(state, plan) {
      state.activePlan = new Plan(plan);
    },
    cancelSubscription(state, isCancelled) {
      state.activeSubscription.is_cancelled = isCancelled;
    },
    setStripeService(state, stripeInstanceCountry) {
      state.stripeService = new StripeService(stripeInstanceCountry);
    },
    setIsBankTransferSelected(state, isBankTransferSelected: boolean) {
      state.isBankTransferSelected = isBankTransferSelected;
    },
    setPaymentMethods(state, paymentMethods: SavedPaymentMethod[]) {
      state.paymentMethods = paymentMethods;
    },
    setLoadingPaymentMethods(state, isLoading: boolean) {
      state.loadingPaymentMethods = isLoading;
    },
    setRetryingPaymentMethods(state, isRetrying: boolean) {
      state.retryingPaymentMethods = isRetrying;
    },
    setPaymentMethodsError(state, hasError: boolean) {
      state.paymentMethodsError = hasError;
    },
    setIsPaidIntentSignup(state, isPaidIntentSignup: boolean) {
      state.isPaidIntentSignup = isPaidIntentSignup;
      setPaidIntentSignupMarker(isPaidIntentSignup);
    },
  },

  actions: {
    async init() {
      // Preloading selected plan, to keep selected plan after a refresh.
      await store.dispatch('subscription/initSubscriptionPlans');
      await store.dispatch('subscription/loadSubscription');
      return Promise.resolve();
    },
    async resetSelectedPlan({ commit }) {
      return commit('subscriptionSelectedPlan', new Plan());
    },
    async initSubscriptionPlans({ commit, state }) {
      if (state.plans.length === 0) {
        commit('loadingState', { loading: 'loadingPlans', isLoading: true });
        return await axios
          .get(AppConfig.getAPIBaseUrl() + '/subscription/stripe/plans/')
          .then((response) => {
            commit('subscriptionPlans', response?.data?.data || []);
            commit('loadingState', { loading: 'loadingPlans', isLoading: false });
          })
          .catch((error: AxiosError) => {
            Sentry.captureException(error);
          });
      }
    },
    async setSubscriptionPlan({ commit }, plan) {
      commit('subscriptionSelectedPlan', plan);
    },
    async setActiveSubscription({ commit }, subscription: Subscription) {
      commit('activeSubscription', subscription);
      commit('activePlan', subscription?.plan ?? new Plan());
    },
    async cancelSubscription({ commit, state }) {
      commit('loadingState', { loading: 'cancelSubscription', isLoading: true });
      return await axios
        .delete(AppConfig.getAPIBaseUrl() + '/subscription/stripe/' + state.activeSubscription.id)
        .then(async () => {
          await store.dispatch('subscription/loadSubscription');
          commit('cancelSubscription', true);
          commit('loadingState', { loading: 'cancelSubscription', isLoading: false });
        })
        .catch((error: AxiosError) => {
          Sentry.captureException(error);
          commit('loadingState', { loading: 'loadingSubscription', isLoading: false });
        });
    },
    // Fetch fresh data from API directly to fill activeSubscription and Plan
    async loadSubscription({ commit }) {
      commit('loadingState', { loading: 'loadingSubscription', isLoading: true });
      return await axios
        .get(AppConfig.getAPIBaseUrl() + '/subscription/stripe/default')
        .then(async (response) => {
          await store.dispatch('subscription/setActiveSubscription', response.data);
          commit('loadingState', { loading: 'loadingSubscription', isLoading: false });
        })
        .catch((error: AxiosError) => {
          Sentry.captureException(error);
          commit('loadingState', { loading: 'loadingSubscription', isLoading: false });
        });
    },
    async reactivateSubscription({ commit }, planId) {
      commit('loadingState', { loading: 'reactivateSubscription', isLoading: true });
      await axios
        .patch(AppConfig.getAPIBaseUrl() + '/subscription/stripe/' + planId, {
          plan_id: planId,
        })
        .then(() => {
          store.dispatch('layout/setSnackbar', {
            type: 'info',
            icon: 'info-system',
            text: 'Your subscription has been resumed successfully',
            isSnackbar: false,
            active: true,
          });
        })
        .catch((error: AxiosError) => {
          Sentry.captureException(error);
          store.dispatch('layout/setSnackbar', {
            type: 'error',
            icon: 'forbidden-system',
            text: 'We failed to reactivate your subscription. Please try again or contact support',
            isSnackbar: false,
            active: true,
          });
        })
        .finally(() => {
          commit('cancelSubscription', false);
          commit('loadingState', { loading: 'reactivateSubscription', isLoading: false });
        });
    },
    async loadPaymentMethods({ commit }, tryAgain = false) {
      if (tryAgain) {
        commit('setRetryingPaymentMethods', true);
        commit('setPaymentMethodsError', false);
      } else {
        commit('setLoadingPaymentMethods', true);
        commit('setPaymentMethodsError', false);
      }

      try {
        const paymentMethods = await PaymentMethodService.listPaymentMethods();
        commit('setPaymentMethods', paymentMethods);
      } catch (error) {
        Sentry.captureException(error);
        commit('setPaymentMethodsError', true);
      }

      commit('setLoadingPaymentMethods', false);
      commit('setRetryingPaymentMethods', false);
    },
    // Inserts a freshly added saved payment method into the store without
    // re-fetching the whole list (so the list isn't hidden behind a spinner).
    // When the new method is the account default, the previous default is
    // demoted so the getters still resolve a single default.
    addPaymentMethod({ commit, state }, method: SavedPaymentMethod) {
      const withoutDuplicate = state.paymentMethods.filter((existing) => existing.id !== method.id);
      const methods = method.is_default
        ? withoutDuplicate.map((existing) => ({ ...existing, is_default: false }))
        : withoutDuplicate;

      commit('setPaymentMethods', [...methods, method]);
    },
    async deletePaymentMethod({ commit, state }, id: string) {
      await PaymentMethodService.deletePaymentMethod(id);
      commit(
        'setPaymentMethods',
        state.paymentMethods.filter((method) => method.id !== id)
      );
    },
    async setDefaultPaymentMethod({ commit, state }, id: string) {
      await PaymentMethodService.setDefaultPaymentMethod(id);

      const previousDefault = state.paymentMethods.find((method) => method.is_default) ?? null;
      let updatedMethods = state.paymentMethods.map((method) => ({
        ...method,
        is_default: method.id === id,
      }));

      // If the previous default is a bank transfer, remove it from the list
      // since it's not possible to use bank transfer anymore
      if (previousDefault?.type === 'bank_transfer') {
        updatedMethods = updatedMethods.filter((method) => method.type !== 'bank_transfer');
      }

      commit('setPaymentMethods', updatedMethods);
    },
  },
  getters: {
    isEnterprise(state) {
      return state?.activePlan?.isEnterprisePlan || false;
    },

    relatedPlans(state) {
      return state.plans.filter((plan: Plan) => plan.product_id === state.selectedPlan.product_id);
    },

    defaultPaymentMethod(state): SavedPaymentMethod | null {
      return getDefaultPaymentMethod(state.paymentMethods);
    },

    otherSavedPaymentMethods(state): SavedPaymentMethod[] {
      return getOtherSavedMethods(state.paymentMethods);
    },
  },
};

export default SubscriptionStore;
